import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, Output, Renderer2, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { Article, Channel, Notification } from '@portals/api';
import { BaseComponent, WindowRef } from '@portals/core';
import { ChannelsState, FollowsState, NotificationsState } from '@portals/store';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { finalize, tap } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { HotToastService } from '@ngneat/hot-toast';
import { DeviceDetectorService } from 'ngx-device-detector';
import { CookieService } from 'ngx-cookie';
import { ContextMenuComponent, ContextMenuDirective, ContextMenuOpenEvent } from '@perfectmemory/ngx-contextmenu';

@Component({
	selector: 'portals-channel-feed',
	templateUrl: './channel-feed.component.html',
	styleUrls: ['./channel-feed.component.scss'],
})
export class ChannelFeedComponent extends BaseComponent implements AfterViewInit {
	@ViewChild('shareDialog') shareDialogRef!: TemplateRef<any>;
	@ViewChild('appContainer', { static: true }) appContainerRef!: ElementRef;
	@ViewChild('mobileView', { static: true }) mobileViewRef!: ElementRef;
	@ViewChild('channelsFeedContent', { static: true }) channelsFeedContentRef!: ElementRef;
	@Input() notifications!: Notification[];
	@Input() channel!: any;
	@Input() portalUser = false;
	@Input() maxFeedHeight = '700px';
	@Input() feedWidth = '370px';
	@Output() shareChannel = new EventEmitter();
	@Output() unfollow = new EventEmitter();
	@Output() onOpen = new EventEmitter<ContextMenuOpenEvent<unknown>>();
	@Output() onMenuItemExecuted = new EventEmitter<string>();
	@Output() onClose = new EventEmitter<'void'>();
	@ViewChild(ContextMenuDirective)
	contextMenuDirective?: ContextMenuDirective<void>;
	@ViewChild(ContextMenuComponent)
	notificationMenu!: ContextMenuComponent<any>;

	feedLoading = false;
	loaderConfig = {
		animationType: ngxLoadingAnimationTypes.pulse,
		primaryColour: `var(--portals-theme-primary)`,
		secondaryColour: `var(--portals-theme-accent)`,
	};
	isDraggable = false;
	showAll = false;
	qrcode!: any;
	isDesktop: boolean;
	isMobile: boolean;
	isTablet: boolean;
	scrollable = false;
	elementType = NgxQrcodeElementTypes.IMG;
	correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;

	constructor(@Inject(MAT_DIALOG_DATA) public data: any, public router: Router, public dialog: MatDialog, private channelState: ChannelsState, private notificationState: NotificationsState, private followsState: FollowsState, private toast: HotToastService, private renderer: Renderer2, private deviceService: DeviceDetectorService, private cookies: CookieService, public window: WindowRef) {
		super();
		this.isDesktop = this.deviceService.isDesktop();
		this.isMobile = this.deviceService.isMobile();
		this.isTablet = this.deviceService.isTablet();
		const windowHeight = this.isMobile ? window.nativeWindow.innerHeight : 600;
		this.maxHeight = `${windowHeight - 304}px`;
	}

	ngAfterViewInit() {
		if (this.data?.notifications?.length > 0) {
			this.notifications = this.data.notifications.filter((notification: Notification) => notification.state === 'LIVE').sort((a: any, b: any) => b.orderIndex - a.orderIndex);
			this.portalUser = this.data;
			this.channel = this.data.channel;
		} else {
			this.notifications = this.notifications.filter((notification: Notification) => notification.state === 'LIVE').sort((a: any, b: any) => b.orderIndex - a.orderIndex);
		}

		const portrait = window.matchMedia('(orientation: portrait)');
		if (this.deviceService.isMobile()) {
			portrait.matches ? null : this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '100%');
		}
		portrait.addEventListener('change', (e) => {
			e.matches ? null : this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '100%');
		});

		if (this.isMobile) {
			portrait.matches ? null : this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '100%');
			// this.renderer.setStyle(this.channelContentRef.nativeElement, 'padding', '0px 28px 0px');
			// this.renderer.setStyle(this.channelContentRef.nativeElement, 'max-height', this.maxHeight);
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'background', 'var(--portals-theme-primary)');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'height', '100vh');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'max-height', window.innerHeight + 'px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'padding', '20px 0p');
		}

		if (this.isTablet) {
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'border-radius', '24px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-height', '610px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-height', '610px');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'width', '100%');
			if (this.deviceService.device == 'iPhone' && this.deviceService.browser == 'Chrome') {
				this.renderer.setStyle(this.mobileViewRef.nativeElement, 'padding-bottom', '10px');
			}
		}

		if (this.isDesktop) {
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '370px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-width', '370px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-height', '650px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-height', '650px');
		}
		this.scrollable = this.channelsFeedContentRef.nativeElement.clientHeight >= 610;
	}

	toggleAll() {
		this.showAll = !this.showAll;
		this.notifications = this.showAll ? this.notifications : this.notifications.filter((notification: Notification) => notification.state === 'LIVE');
	}

	openDialog() {
		this.dialog.open(this.shareDialogRef, {
			panelClass: 'share-dialog',
			backdropClass: 'share-backdrop',
		});
	}

	shareConfirm() {
		const toaster = this.toast.success('Channel share url copied to your clipboard!', {
			style: {
				border: `4px solid var(--portals-theme-popover)`,
				padding: '16px',
				margin: '20px 20px',
				color: 'var(--portals-theme-on-popover)',
				background: 'var(--portals-theme-popover)',
			},
			iconTheme: {
				primary: 'var(--portals-theme-primary)',
				secondary: 'var(--portals-theme-accent)',
			},
		});
		setTimeout(async () => {
			await toaster.close();
			this.toast.close();
		}, 1000);
	}

	unfollowChannel() {
		this.feedLoading = true;
		this.subscribe(
			this.followsState.delete(this.channel.id).pipe(
				tap(() => {
					this.feedLoading = true;
					const followList = this.cookies.get('follows') || '[]';
					const parsedFollowList = JSON.parse(followList);
					const updatedFollowList = parsedFollowList.filter((item: string) => item !== this.channel.id.toString());
					const follows = JSON.stringify(updatedFollowList);
					this.cookies.put('follows', follows);
				}),
				finalize(() => {
					this.router.navigate(['..']);
				})
			)
		);
	}

	toggleDraggable() {
		this.isDraggable = !this.isDraggable;
	}
	drop(notification: CdkDragDrop<Array<Notification>>) {
		if (notification.previousIndex - notification.currentIndex != 0) {
			const oldIndex = notification.item.data.orderIndex;
			const newIndex = notification.item.data.orderIndex + (notification.previousIndex - notification.currentIndex);
			this.reorderNotification(oldIndex, newIndex, notification.item.data.channel.id, notification.previousIndex, notification.currentIndex);
			moveItemInArray(this.notifications, notification.previousIndex, notification.currentIndex);
		}
	}

	handleMenu(notification: any, action: string) {
		this.onMenuItemExecuted.next(notification);

		let oldIndex: number;
		let newIndex: number;
		const channel = notification.value.channel.id;
		const index = this.notifications.findIndex((index) => index.id === notification.value.id);

		switch (action) {
			case 'top':
				newIndex = <number>this.notifications[0].orderIndex;
				oldIndex = <number>notification.value.orderIndex;
				this.reorderNotification(oldIndex, newIndex, channel, index, 0);
				moveItemInArray(this.notifications, index, 0);
				break;
			case 'bottom':
				newIndex = <number>this.notifications[this.notifications.length - 1].orderIndex;
				oldIndex = <number>notification.value.orderIndex;
				this.reorderNotification(oldIndex, newIndex, channel, index, this.notifications.length);
				moveItemInArray(this.notifications, index, this.notifications.length);
				break;
			case 'up':
				newIndex = <number>this.notifications[index - 1].orderIndex;
				oldIndex = <number>notification.value.orderIndex;
				this.reorderNotification(oldIndex, newIndex, channel, index, index - 1);
				moveItemInArray(this.notifications, index, index - 1);
				break;
			case 'down':
				newIndex = <number>this.notifications[index + 1].orderIndex;
				oldIndex = <number>notification.value.orderIndex;
				this.reorderNotification(oldIndex, newIndex, channel, index, index + 1);
				moveItemInArray(this.notifications, index, index + 1);
				break;
			default:
				break;
		}
	}

	open(value: ContextMenuOpenEvent<unknown>) {
		this.onOpen.next(value);
	}

	close() {
		this.onClose.next('void');
	}

	private reorderNotification(oldIndex: number, newIndex: number, channel: number, localOldIndex: number, localNewIndex: number) {
		this.subscribe(this.notificationState.reorderNotification(oldIndex, newIndex, channel));

		this.notifications.slice(Math.min(localOldIndex, localNewIndex), Math.max(localOldIndex, localNewIndex) + 1).forEach((notification) => {
			if (notification.orderIndex == oldIndex) {
				notification.orderIndex = newIndex;
			} else if (newIndex < oldIndex && notification.orderIndex < oldIndex) {
				notification.orderIndex += 1;
			} else if (newIndex > oldIndex && notification.orderIndex > oldIndex) {
				notification.orderIndex -= 1;
			}
		});
	}
}
